import PropTypes from 'prop-types';
import React, { useEffect, useRef, memo } from 'react';
import { List } from 'immutable';
import classNames from 'classnames';

import { IconButton } from '../../../helpers/IconButton';
import config from '../../../../config';
import { execIfFunction } from '../../../../utils/isFunction';
import { TemplateTableRow } from './TemplateTableRow';
import { OneColumnTableRow } from './OneColumnTableRow';

import style from './style.module.css';

const types = config.values.getIn(['project', 'types']);

/**
 * Table with only one column component.
 *
 * @param props {Object} Component props.
 */
const OneColumnTableComponent = ({
  title,
  rows,
  contextId,
  suggestions,
  type,
  onRowClick,
  onRowDoubleClick,
  onRowInsert,
  onSuggestionClose,
  onTextChange,
  onSuggestionSelect,
  onTemplateUpdate,
  onKeyPress,
  skipMenu,
  changed = false,
}) => {
  const containerRef = useRef(null);
  const inputElementRef = useRef(null);

  useEffect(() => {
    if (rows.findLastIndex(r => r.get('id') === -1) > 0) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }

    if (inputElementRef.current) {
      inputElementRef.current.focus();
    }
  }, [rows]);

  const handleRowInsert = event => {
    event.preventDefault();
    event.stopPropagation();
    execIfFunction(onRowInsert, type);
  };

  const noItems = rows.size === 0 ? <div className={style.message}>No items to display</div> : null;
  let rowContents = null;

  if (type === types.get('TEMPLATES')) {
    rowContents = rows.map((row, index) => (
      <TemplateTableRow
        key={index}
        contextId={contextId}
        onKeyPress={onKeyPress}
        onRowClick={onRowClick}
        onTemplateUpdate={onTemplateUpdate}
        onTextChange={onTextChange}
        row={row}
        setInputElement={input => {
          inputElementRef.current = input;
        }}
        type={type}
      />
    ));
  } else {
    rowContents = rows.map((row, index) => (
      <OneColumnTableRow
        key={index}
        contextId={contextId}
        onRowClick={onRowClick}
        onRowDoubleClick={onRowDoubleClick}
        onSuggestionClose={onSuggestionClose}
        onSuggestionSelect={onSuggestionSelect}
        onTextChange={onTextChange}
        row={row}
        setInputElement={input => {
          inputElementRef.current = input;
        }}
        skipMenu={skipMenu}
        suggestions={suggestions}
        type={type}
      />
    ));
  }

  const changedClass = classNames({ changed });

  return (
    <div className={style.host}>
      <div ref={containerRef} className={style.tableWrapper}>
        <table className="table table-bordered table-hover">
          <thead>
            <tr>
              <th>
                <span className={style.title}>
                  <IconButton
                    className={style.addButton}
                    icon="plus"
                    onClick={handleRowInsert}
                    size="sm"
                    title="Add New"
                    variant="primary"
                  />
                  <span className={changedClass}>{title}</span>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>{rowContents}</tbody>
        </table>
        {noItems}
      </div>
    </div>
  );
};

OneColumnTableComponent.propTypes = {
  contextId: PropTypes.string.isRequired,
  onKeyPress: PropTypes.func,
  onRowClick: PropTypes.func,
  onRowDoubleClick: PropTypes.func,
  onRowInsert: PropTypes.func,
  onSuggestionClose: PropTypes.func,
  onSuggestionSelect: PropTypes.func,
  onTemplateUpdate: PropTypes.func,
  onTextChange: PropTypes.func,
  rows: PropTypes.instanceOf(List).isRequired,
  suggestions: PropTypes.instanceOf(List),
  title: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
};

export const OneColumnTable = memo(OneColumnTableComponent);
